import { DeviceKey, IfKey } from "./Keys";
import { Link, LinkType } from "./Link";
import { DeviceCategory } from "./DeviceCategory";
import { IfTable, IfEntry } from "../InfoSets/IfTable";
import { Device } from "./Device";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { Dictionary } from "../Utils";
import { CategoryMap, CategoryMapTable } from "../Utils/CategoryMap";

export const IntegrationNames = {
	AssetPanda: "AssetPanda",
	Halo: "Halo",
	Hudu: "Hudu",
	ITGlue: "ITGlue",
	Jira: "Jira",
	Lansweeper: "Lansweeper",
	LansweeperCloud: "Lansweeper[Cloud]",
	PatchManager: "PatchManager",
	ServiceNow: "ServiceNow",
	NinjaOne: "NinjaOne",
	Lucidchart: "Lucidchart",
	Slack: "Slack",
} as const;

export class Integration implements ObjectSerializer {
	public Name: string
	public get HasName(): boolean
    {
		return !!this.Name;
    }

	public Description: string;
	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public ServerURL: string;
	public get HasServerURL(): boolean {
		return !!this.ServerURL;
	}

	public ApiServerURL: string;
	public get HasApiServerURL(): boolean {
		return !!this.ApiServerURL;
	}

	public TokenServerURL: string;
	public get HasTokenServerURL(): boolean {
		return !!this.TokenServerURL;
	}

	public ClientID: string;
	public get HasClientID(): boolean {
		return !!this.ClientID;
	}

	public ClientSecret: string;
	public get HasClientSecret(): boolean {
		return !!this.ClientSecret;
	}

	public ApiKey: string;
	public get HasApiKey(): boolean {
		return !!this.ApiKey;
	}

	public Username: string;
	public get HasUsername(): boolean {
		return !!this.Username;
	}

	public Password: string;
	public get HasPassword(): boolean {
		return !!this.Password;
	}

	public DbServer: string;
	public get HasDbServer(): boolean {
		return !!this.DbServer;
	}

	public DbName: string;
	public get HasDbName(): boolean {
		return !!this.DbServer;
	}

	public Webhooks: string;
	public get HasWebhooks(): boolean {
		return !!this.Webhooks;
	}

	public CategoryMap: CategoryMapTable;
	public get HasCategoryMap(): boolean {
		if (this.CategoryMap == null) {
			return false;
		}
		return (this.CategoryMap.Count() > 0);
	}

	public Enabled: boolean = false;
	public UseMacAddressLookup: boolean = false;
	public UseIpAddressLookup: boolean = false;

	constructor(name: string) {
		this._Init();
		this.Name = name;

		if (this.Name === IntegrationNames.Lucidchart) {
			this.ServerURL = "https://lucid.app/";
			this.ApiServerURL = "https://api.lucid.co/";
		}
	}

	private _Init(): void {
		this.Name = "";
		this.Description = "";
		this.ServerURL = "https://lucid.app/";
		this.ApiServerURL = "https://api.lucid.co/";
		this.TokenServerURL = "";
		this.ApiKey = "";
		this.ClientID = "";
		this.ClientSecret = "";
		this.Username = "";
		this.Password = "";
		this.DbServer = "";
		this.DbName = "";
		this.Webhooks = "";
		this.CategoryMap = new CategoryMapTable();
		this.Enabled = false;
		this.UseMacAddressLookup = false;
		this.UseIpAddressLookup = false;
	}

	public Equals(obj: any): boolean {
		if (obj == null) {
			return false;
		}

		if (obj === this) {
			return true;
		}

		if (this.constructor !== obj.constructor) {
			return false;
		}

		const other: Integration = obj as Integration;

		return this.Name === other.Name;
	}

	public Copy(other: Integration): void {
		if (other == null) {
			return;
		}

		this.Name = other.Name;
		this.Description = other.Description;
		this.ServerURL = other.ServerURL;
		this.ApiServerURL = other.ApiServerURL;
		this.TokenServerURL = other.TokenServerURL;
		this.ApiKey = other.ApiKey;
		this.ClientID = other.ClientID;
		this.ClientSecret = other.ClientSecret;
		this.Username = other.Username;
		this.Password = other.Password;
		this.DbServer = other.DbServer;
		this.DbName = other.DbName;
		this.Webhooks = other.Webhooks;
		this.Enabled = other.Enabled;
		this.UseMacAddressLookup = other.UseMacAddressLookup;
		this.UseIpAddressLookup = other.UseIpAddressLookup;

		this.CategoryMap.Clear();
		if (other.HasCategoryMap) {
			this.CategoryMap = other.CategoryMap;
		}
	}

	//public hashCode(): number {
	//	return this.Name.hashCode();
	//}

	public Write(obj: any, context: any): void {
		obj.integration = {};
		obj = obj.integration;

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasDescription)
			obj.description = this.Description;

		if (this.HasServerURL)
			obj.server_url = this.ServerURL;

		if (this.HasApiServerURL)
			obj.api_server_url = this.ApiServerURL;

		if (this.HasTokenServerURL)
			obj.token_server_url = this.TokenServerURL;

		if (this.HasApiKey)
			obj.api_key = this.ApiKey;

		if (this.HasClientID)
			obj.client_id = this.ClientID;

		if (this.HasClientSecret)
			obj.client_secret = this.ClientSecret;

		if (this.HasUsername)
			obj.user_name = this.Username;

		if (this.HasPassword)
			obj.password = this.Password;

		if (this.HasDbServer)
			obj.db_server = this.DbServer;

		if (this.HasDbName)
			obj.db_name = this.DbName;

		if (this.HasWebhooks)
			obj.webhooks = this.Webhooks;

		if (this.HasCategoryMap)
			this.CategoryMap.Write(obj, context);

		obj.enabled = ValueEncoder.EncodeBoolean(this.Enabled);
		obj.use_mac_lookup = ValueEncoder.EncodeBoolean(this.UseMacAddressLookup);
		obj.use_ip_lookup = ValueEncoder.EncodeBoolean(this.UseIpAddressLookup);
	}

	public Read(obj: any, context: any): void {
		if (!obj.integration)
			throw new Error();
		obj = obj.integration;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.server_url !== undefined)
			this.ServerURL = obj.server_url;

		if (obj.api_server_url !== undefined)
			this.ApiServerURL = obj.api_server_url;

		if (obj.token_server_url !== undefined)
			this.TokenServerURL = obj.token_server_url;

		if (obj.api_key !== undefined)
			this.ApiKey = obj.api_key;

		if (obj.client_id !== undefined)
			this.ClientID = obj.client_id;

		if (obj.client_secret !== undefined)
			this.ClientSecret = obj.client_secret;

		if (obj.user_name !== undefined)
			this.Username = obj.user_name;

		if (obj.password !== undefined)
			this.Password = obj.password;

		if (obj.db_server !== undefined)
			this.DbServer = obj.db_server;

		if (obj.db_name !== undefined)
			this.DbName = obj.db_name;

		if (obj.webhooks !== undefined)
			this.Webhooks = obj.webhooks;

		if (obj.category_map_table !== undefined)
			this.CategoryMap.Read(obj, context);

		if (obj.enabled !== undefined)
			this.Enabled = ValueEncoder.DecodeBoolean(obj.enabled);

		if (obj.use_mac_lookup !== undefined)
			this.UseMacAddressLookup = ValueEncoder.DecodeBoolean(obj.use_mac_lookup);

		if (obj.use_ip_lookup !== undefined)
			this.UseIpAddressLookup = ValueEncoder.DecodeBoolean(obj.use_ip_lookup);
	}
}

export class IntegrationTable {
	private _integrationTable: Dictionary<string, Integration>;

	constructor() {
		this._Init();
	}

	private _Init(): void {
		this._integrationTable = new Dictionary<string, Integration>();
	}

	public Add(entry: Integration): boolean {
		if (entry == null) {
			return false;
		}

		const oldEntry = this._integrationTable[entry.Name];

		if (!oldEntry) {
			this._integrationTable.Add(entry.Name, entry);
		} else {
			// Update the entry
			Object.assign(oldEntry, entry);
		}

		return true;
	}

	public AddByName(name: string): Integration {
		let newEntry = this.Find(name);

		if (newEntry == null) {
			newEntry = new Integration(name);
			this._integrationTable[name] = newEntry;
		}

		return newEntry;
	}

	public Clear(): void {
		this._integrationTable = new Dictionary<string, Integration>();
	}

	public get Count(): number {
		return this._integrationTable.Count;
	}

	public Find(name: string): Integration | null {
		return this._integrationTable.Item(name);
	}

	public Remove(entry: Integration): boolean {
		if (entry == null) {
			return false;
		}

		if (this._integrationTable.hasOwnProperty(entry.Name)) {
			delete this._integrationTable[entry.Name];
			return true;
		}

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.integration_table = {};
		obj = obj.integration_table;

		obj.entries = [];

		if (this.Count > 0) {
			ObjSerUtils.WriteObjSerCollection(obj.entries, this._integrationTable.Values(), context); 
		}
	}

	public Read(obj: any, context: any): void {
		this.Clear();
		if (!obj.integration_table)
			throw new Error();

		if (obj.integration_table?.entries) {
			obj.integration_table.entries.forEach((entry: any) => {
				const integration = new Integration(entry.name);
				integration.Read(entry, context);
				this.Add(integration);
			}, this);
		}
	}
}
